import { useEffect, useRef, useCallback } from 'react';
import { useAuth0 } from '../components/auth/DevAuth0Provider';

/**
 * Custom hook to handle automatic logout after 60 minutes of inactivity
 * Tracks user activity and automatically logs out inactive users
 * Enhanced with better edge case handling and token validation
 */
const useSessionTimeout = () => {
  const { logout, isAuthenticated, validateSession, isTokenExpired } = useAuth0();
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningTimeoutRef = useRef(null);

  // Activity timeout duration: 60 minutes in milliseconds
  const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes
  const WARNING_TIME = 5 * 60 * 1000; // 5 minutes before timeout

  // Reset the timeout when user is active
  const resetTimeout = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Set warning timeout (5 minutes before actual timeout)
    warningTimeoutRef.current = setTimeout(() => {
      // console.log('Session warning: User will be logged out in 5 minutes due to inactivity');
      // You could show a warning modal here if needed
    }, INACTIVITY_TIMEOUT - WARNING_TIME);

    // Set actual timeout
    timeoutRef.current = setTimeout(() => {
      // console.log('Session timeout: User inactive for 60 minutes, logging out...');
      
      // Double-check token validity before logout
      if (validateSession && !validateSession()) {
        // console.log('Token already expired, clearing session...');
        return;
      }
      
      logout({ logoutReason: 'session_timeout' });
    }, INACTIVITY_TIMEOUT);
  }, [logout, validateSession]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Only check session validity if user is authenticated
    if (isAuthenticated && validateSession && !validateSession()) {
      // console.log('Session invalid during activity, not resetting timeout');
      return;
    }
    resetTimeout();
  }, [resetTimeout, validateSession, isAuthenticated]);

  // Periodic session validation (every 5 minutes)
  const periodicValidation = useCallback(() => {
    if (isAuthenticated && validateSession) {
      if (!validateSession()) {
        // console.log('Periodic validation failed, session expired');
        logout({ logoutReason: 'token_expired' });
      }
    }
  }, [isAuthenticated, validateSession, logout]);

  useEffect(() => {
    // Only set up timeout if user is authenticated
    if (!isAuthenticated) {
      // Clear timeouts if user is not authenticated
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      return;
    }

    // Set up activity listeners
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus', // Add focus events
      'blur'   // Add blur events
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Set up periodic validation (every 5 minutes)
    const validationInterval = setInterval(periodicValidation, 5 * 60 * 1000);

    // Initialize timeout
    resetTimeout();

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });

      // Clear timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }

      // Clear validation interval
      clearInterval(validationInterval);
    };
  }, [isAuthenticated, handleActivity, resetTimeout, periodicValidation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);
};

export default useSessionTimeout;
